import { appShareImageUrl } from "@/lib/shareImage";

const BACKEND = process.env.NEXT_PUBLIC_BACKEND_URL;

export type WechatShareCardConfig = {
  title: string;
  description?: string;
  link?: string;
  imageUrl?: string;
};

export function isWechatInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /MicroMessenger/i.test(navigator.userAgent);
}

type WxApi = {
  config: (cfg: {
    debug: boolean;
    appId: string;
    timestamp: string;
    nonceStr: string;
    signature: string;
    jsApiList: string[];
  }) => void;
  ready: (cb: () => void) => void;
  error: (cb: (res: { errMsg?: string }) => void) => void;
  updateAppMessageShareData: (cfg: {
    title: string;
    desc: string;
    link: string;
    imgUrl: string;
    success?: () => void;
    fail?: (err: unknown) => void;
  }) => void;
  updateTimelineShareData: (cfg: {
    title: string;
    link: string;
    imgUrl: string;
    success?: () => void;
    fail?: (err: unknown) => void;
  }) => void;
};

type WeixinJSBridge = {
  on: (event: string, cb: () => void) => void;
  invoke: (
    api: string,
    data: Record<string, string>,
    cb?: (res: { err_msg?: string }) => void,
  ) => void;
};

function getWx(): WxApi | undefined {
  return (window as unknown as { wx?: WxApi }).wx;
}

/** 动态加载微信 JS-SDK（jweixin-1.6.0.js），并等待 wx 对象可用 */
export async function loadWechatJSSDK(): Promise<void> {
  if (typeof document === "undefined") return;
  if (getWx()) return;

  const src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
  let script = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

  if (!script) {
    script = document.createElement("script");
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  await new Promise<void>((resolve, reject) => {
    const done = () => {
      if (getWx()) resolve();
      else reject(new Error("微信 JS-SDK 未初始化"));
    };
    if (getWx()) {
      resolve();
      return;
    }
    script!.addEventListener("load", done, { once: true });
    script!.addEventListener("error", () => reject(new Error("微信 JS-SDK 加载失败")), {
      once: true,
    });
  });
}

function applyShareData(wx: WxApi, config: WechatShareCardConfig, shareLink: string, imageUrl: string): void {
  const description = config.description ?? "你的数学学习助手";

  wx.updateAppMessageShareData({
    title: config.title,
    desc: description,
    link: shareLink,
    imgUrl: imageUrl,
  });

  wx.updateTimelineShareData({
    title: config.title,
    link: shareLink,
    imgUrl: imageUrl,
  });
}

/** 旧版微信内置浏览器的菜单分享兜底 */
function registerWeixinJSBridgeFallback(
  config: WechatShareCardConfig,
  shareLink: string,
  imageUrl: string,
): void {
  const description = config.description ?? "你的数学学习助手";

  const bind = () => {
    const bridge = (window as unknown as { WeixinJSBridge?: WeixinJSBridge }).WeixinJSBridge;
    if (!bridge) return;

    bridge.on("menu:share:appmessage", () => {
      bridge.invoke(
        "sendAppMessage",
        {
          title: config.title,
          desc: description,
          link: shareLink,
          img_url: imageUrl,
        },
        () => undefined,
      );
    });

    bridge.on("menu:share:timeline", () => {
      bridge.invoke(
        "shareTimeline",
        {
          title: config.title,
          link: shareLink,
          img_url: imageUrl,
        },
        () => undefined,
      );
    });
  };

  const bridge = (window as unknown as { WeixinJSBridge?: WeixinJSBridge }).WeixinJSBridge;
  if (bridge) bind();
  else document.addEventListener("WeixinJSBridgeReady", bind, false);
}

/** 在微信内置浏览器中配置「发送给朋友 / 分享到朋友圈」卡片 */
export async function configureWechatShareCard(config: WechatShareCardConfig): Promise<void> {
  if (typeof window === "undefined" || !BACKEND) return;
  if (!isWechatInAppBrowser()) return;

  const currentUrl = window.location.href.split("#")[0];
  const shareLink = config.link ?? currentUrl;
  const imageUrl = config.imageUrl ?? appShareImageUrl();

  registerWeixinJSBridgeFallback(config, shareLink, imageUrl);

  const encodedUrl = encodeURIComponent(currentUrl);
  const r = await fetch(`${BACKEND}/wechat/signature?url=${encodedUrl}`, { cache: "no-store" });
  if (!r.ok) {
    throw new Error(`微信签名失败: ${r.status}`);
  }

  const sig = (await r.json()) as {
    appId: string;
    timestamp: string;
    nonceStr: string;
    signature: string;
  };

  await loadWechatJSSDK();
  const wx = getWx();
  if (!wx) throw new Error("微信 JS-SDK 不可用");

  await new Promise<void>((resolve, reject) => {
    wx.config({
      debug: false,
      appId: sig.appId,
      timestamp: sig.timestamp,
      nonceStr: sig.nonceStr,
      signature: sig.signature,
      jsApiList: ["updateAppMessageShareData", "updateTimelineShareData"],
    });

    wx.ready(() => {
      applyShareData(wx, config, shareLink, imageUrl);
      resolve();
    });

    wx.error((res) => {
      reject(new Error(res.errMsg ?? "wx.config 失败"));
    });
  });
}
